/**
 * <BottomTab> — presentational bottom navigation bar (molecule, L0.1).
 *
 * The DS recreation of the app's current bottom nav (`BottomTabsNavigator.js`). It is
 * STATELESS: the parent owns which tab is active and reacts to `onTabChange`.
 *
 * `tabs` is an array of `TabKey`s (prefer `TABS_BY_TYPE[tabType]` from `tabRegistry.ts`);
 * each key is resolved to its `{ icon, label }` there. Every tab is rendered by the
 * `<TabBarButton>` atom — the bar itself only owns the surface/border and the row layout.
 * L'onglet actif se reconnaît à sa couleur ET à son soulignement.
 *
 * Largeur : la surface prend toute la largeur, mais les onglets forment un **groupe compact
 * centré**, pas une rangée étirée bord à bord. Écartés sur toute une colonne de contenu, quatre
 * onglets se lisent comme quatre îlots sans rapport ; groupés, ils se lisent comme une barre.
 */
import React from "react"
import { View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { TabBarButton } from "../../atoms/Buttons/TabBarButton"
import { TAB_ITEMS, type TabKey } from "./tabRegistry"

export type BottomTabProps = {
    /** Ordered tab keys to render — resolved to icon + label via `TAB_ITEMS`. */
    tabs: TabKey[]
    /** Key of the currently-active tab (controlled by the parent). */
    activeKey: TabKey
    onTabChange: (key: TabKey) => void
    /** Color for the active tab — defaults to `role`, la couleur de l'univers courant. */
    activeColor?: SemanticColorKey
    /** Color for inactive tabs — defaults to `text-muted`. */
    inactiveColor?: SemanticColorKey
    /** Un compteur par onglet (messages non lus, live imminent). Aucun par défaut. */
    badges?: Partial<Record<TabKey, number>>
    /** L'adresse de chaque onglet : sur le web, il devient un vrai lien (`<TabBarButton href>`). */
    hrefs?: Partial<Record<TabKey, string>>
    testID?: string
}

export function BottomTab(props: BottomTabProps) {
    const {
        tabs,
        activeKey,
        onTabChange,
        activeColor = "role",
        inactiveColor = "text-muted",
        badges,
        hrefs,
        testID,
    } = props
    // La barre est la dernière surface de l'écran : elle laisse la place de l'indicateur
    // d'accueil sous les onglets (sur le web l'inset vaut zéro).
    const insets = useSafeAreaInsets()

    return (
        <View testID={testID} style={styles.bar(insets.bottom)}>
            <Row justify="center" align="center" gap="6" style={styles.row}>
                {tabs.map((key) => {
                    const { icon, label } = TAB_ITEMS[key]
                    return (
                        <TabBarButton
                            key={key}
                            iconName={icon}
                            label={label}
                            active={key === activeKey}
                            onPress={() => onTabChange(key)}
                            activeColor={activeColor}
                            inactiveColor={inactiveColor}
                            badgeCount={badges?.[key]}
                            href={hrefs?.[key]}
                        />
                    )
                })}
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    bar: (bottomInset: number) => ({
        backgroundColor: theme.colors["surface-raised"],
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-subtle"],
        paddingBottom: bottomInset,
    }),
    row: {
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
        flexWrap: "wrap",
    },
}))
